import { useState } from 'react'

import DeckService from 'services/deck.service'
import ToastService from 'services/toast.service'

import { BRACKET_NAME, CardType } from 'types/deck'
import { BracketInfoSearchType } from 'services/apiTypes/card.types'
import { generateTabState } from 'types/searchV2'

import { useActionless, useAppSelector } from 'redux/hooks'
import { selectCalculatedEDHBracket, selectCalculatedEDHBracketDetails, selectCanEditDeck } from 'redux/deck/selectors'
import { SET_ACTIVE_STATE } from 'redux/active/actions/types'
import { SET_DECK_STATE } from 'redux/deck/actions/types'

import PhatButton from 'components/formElements/PhatButton'
import Link from 'components/elements/Link'
import { BracketBlurbs } from 'components/misc/BracketInfoToolTip'

import styles from './estimatedBracketOverlayContent.module.scss'

type Props = {
  onForceClose: () => void
}

// Moving the content to it's own component so we don't render this until the overlay is open
const EstimatedBracketOverlayContent = ({ onForceClose }: Props) => {
  const [updatingDeckDetails, setUpdatingDeckDetails] = useState(false)

  const deckId = useAppSelector(state => state.deck.id)
  const edhBracket = useAppSelector(state => state.deck.edhBracket)
  const canEditDeck = useAppSelector(selectCanEditDeck)
  const calculatedBracket = useAppSelector(selectCalculatedEDHBracket)
  const calculatedBracketDetails = useAppSelector(selectCalculatedEDHBracketDetails)

  const [setActiveState, setDeckState] = useActionless(SET_ACTIVE_STATE, SET_DECK_STATE)

  const handleViewCardSearch = (bracketInfoSearchType: BracketInfoSearchType) => {
    onForceClose()

    // Timeout fixes a bug where with opening the search overlay as the non root overlay
    // TODO - come back and actually fix the bug later
    setTimeout(() => {
      const newSearchTab = generateTabState.archidektSearch()

      // @ts-ignore
      newSearchTab.meta.bracketInfo = bracketInfoSearchType

      setActiveState({ searchOpen: newSearchTab })
    }, 500)
  }

  const handleViewCombos = () => {
    onForceClose()

    // Timeout fixes a bug where with opening the search overlay as the non root overlay
    setTimeout(() => {
      const newSearchTab = generateTabState.spellbookCombo()

      setActiveState({ searchOpen: newSearchTab })
    }, 500)
  }

  const handleSetBracketExplicity = () => {
    setUpdatingDeckDetails(true)

    return DeckService.update(deckId, { edhBracket: calculatedBracket })
      .then(() => {
        setDeckState({ edhBracket })

        ToastService.create('Deck bracket updated', 'Deck Service', 'success')
      })
      .catch(() => ToastService.create('Failed to update deck bracket', 'Deck Service', 'error'))
      .finally(() => setUpdatingDeckDetails(false))
  }

  let BracketBlurb = BracketBlurbs.BracketOne

  if (calculatedBracket === 2) BracketBlurb = BracketBlurbs.BracketTwo
  if (calculatedBracket === 3) BracketBlurb = BracketBlurbs.BracketThree
  if (calculatedBracket === 4) BracketBlurb = BracketBlurbs.BracketFour

  const bracketName = BRACKET_NAME[calculatedBracket]

  return (
    <div className={styles.overlayContent}>
      <div className={styles.header}>
        <h2>Estimated Commander Bracket Details</h2>

        {canEditDeck && (
          <PhatButton
            color="blue"
            onClick={handleSetBracketExplicity}
            disabled={updatingDeckDetails || edhBracket === calculatedBracket}>
            {updatingDeckDetails ? 'Saving Commander Bracket...' : `Set deck to ${bracketName}`}
          </PhatButton>
        )}
      </div>

      <BracketBlurb className={styles.blurb} />

      <p className={styles.description}>
        Archidekt is estimating your deck to have a Commander Bracket of{' '}
        <Link
          className={styles.orange}
          href="https://edhrec.com/articles/wotc-introduces-new-bracket-system-for-edh"
          about="_blank">
          {bracketName}
        </Link>
        . If you disagree with this assessment, you can set your deck's bracket in the deck settings. Keep in mind that,
        when you explicitly set your deck's bracket, Archidekt will use our guidelines to determine the legality of your
        deck. If you have any issues, feel free to report them on our{' '}
        <Link href="/forum/General/Development" className={styles.orange} target="_blank">
          forums
        </Link>{' '}
        or{' '}
        <Link className={styles.orange} href="https://discord.gg/GyAAANU8uJ" target="_blank">
          Discord server
        </Link>
        .
      </p>

      <div className={styles.disclaimer}>
        **The Commander Bracket of a deck is NOT a strict power ranking of a deck. It is a tool for you to better
        communicate what is in your deck, and what it is trying to do with other players.
      </div>

      <h3>Estimated Commander Bracket reasoning</h3>

      <div className={styles.reasoning}>
        {calculatedBracketDetails.gameChangers.length == 0 &&
          calculatedBracketDetails.extraTurns.length == 0 &&
          calculatedBracketDetails.massLandDenial.length == 0 &&
          // calculatedBracketDetails.tutors.length == 0 &&
          calculatedBracketDetails.atomicCombos.length == 0 &&
          calculatedBracketDetails.potentialCombos.length == 0 && (
            <div className={`${styles.blurb} ${styles.noMaxWidth}`}>
              <span className={styles.noContent}>
                {calculatedBracket === 1 &&
                  `We estimate your deck to have a Commander Bracket of ${BRACKET_NAME[1]} because it doesn't contain any cards that would push it into a higher bracket.`}
                {calculatedBracket === 2 &&
                  `We estimate your deck to have a Commander Bracket of ${BRACKET_NAME[2]}, but it could qualify as an ${BRACKET_NAME[1]} if that's your intent.`}
              </span>
            </div>
          )}

        <BracketReasoning
          cards={calculatedBracketDetails.gameChangers}
          typeOfCard="Game Changer card(s)"
          onClick={() => handleViewCardSearch('gameChangers')}
        />

        <BracketReasoning
          cards={calculatedBracketDetails.extraTurns}
          typeOfCard="extra turn card(s)"
          gotchas={[
            calculatedBracket < 4
              ? `If your deck chains extra turns, your deck should be ${BRACKET_NAME[4]} or ${BRACKET_NAME[5]}`
              : null,
          ]}
          onClick={() => handleViewCardSearch('extraTurns')}
        />

        <BracketReasoning
          cards={calculatedBracketDetails.massLandDenial}
          typeOfCard="mass land denial card(s)"
          onClick={() => handleViewCardSearch('massLandDenial')}
        />

        {/* <BracketReasoning
          cards={calculatedBracketDetails.tutors}
          typeOfCard="non-land tutor card(s)"
          gotchas={[
            calculatedBracket < 4 && calculatedBracketDetails.tutors.length > 3
              ? `This deck seems to have a high number of tutors. Consider if your deck may be ${BRACKET_NAME[4]} or ${BRACKET_NAME[5]}.`
              : null,
          ]}
          onClick={() => handleViewCardSearch('tutors')}
        /> */}

        <BracketReasoning
          cards={calculatedBracketDetails.atomicCombos}
          typeOfCard="two-card infinite combo pieces"
          gotchas={[`If this is a late-game combo, your deck may be a Bracket: ${BRACKET_NAME[3]}`]}
          onClick={handleViewCombos}
        />

        <BracketReasoning
          cards={calculatedBracketDetails.potentialCombos}
          typeOfCard="potential two-card infinite combo pieces"
          extendedMessage="(does not influence estimated bracket placement)"
          gotchas={[
            'These are cards that can create 2-card infinite combos when specific conditions are met (usually other cards).',
          ]}
          onClick={handleViewCombos}
        />
      </div>

      <h3>Additional Commander Bracket info</h3>

      <p>
        While the list of Game Changers is officially maintained by Wizards of the Coast, extra turn cards, mass land
        denial, and combos are not explicitly enumerated. Archidekt (in collaboration with{' '}
        <Link className={styles.orange} href="https://commanderspellbook.com/" target="_blank">
          Commander Spellbook
        </Link>{' '}
        for combos) curates lists for these cards.
      </p>

      <p>
        If you feel something is missing from any of these lists or that we include a card that doesn't belong, please
        reach out with your suggestion in our community{' '}
        <Link className={styles.orange} href="https://discord.gg/GyAAANU8uJ" target="_blank">
          Discord server
        </Link>
        .
      </p>

      <div className={styles.viewCardSearchButtons}>
        <PhatButton icon="arrow right" onClick={() => handleViewCardSearch('gameChangers')}>
          View Game Changers
        </PhatButton>
        <PhatButton icon="arrow right" onClick={() => handleViewCardSearch('extraTurns')}>
          View extra turn cards
        </PhatButton>
        <PhatButton icon="arrow right" onClick={() => handleViewCardSearch('massLandDenial')}>
          View mass land denial cards
        </PhatButton>
        {/* <PhatButton icon="arrow right" onClick={() => handleViewCardSearch('tutors')}>
          View tutor cards
        </PhatButton> */}
        <PhatButton icon="arrow right" onClick={handleViewCombos}>
          View my deck's combos
        </PhatButton>
      </div>
    </div>
  )
}

export default EstimatedBracketOverlayContent

const BracketReasoning = ({
  cards,
  typeOfCard,
  extendedMessage,
  gotchas = [],
  onClick,
}: {
  cards: CardType[]
  typeOfCard: string
  extendedMessage?: string
  gotchas?: (string | null)[]
  onClick?: () => void
}) => {
  const [setDeckState] = useActionless(SET_DECK_STATE)

  if (!cards.length) return null

  return (
    <div className={styles.blurb}>
      <div>
        You have {cards.length}{' '}
        <button className={styles.typeOfCardButton} onClick={onClick} disabled={!onClick}>
          {typeOfCard}
        </button>{' '}
        in your deck{extendedMessage && ` ${extendedMessage}`}
      </div>

      <ul>
        {cards.map(card => (
          <li key={card.id}>
            <button className={styles.cardTrigger} onClick={() => setDeckState({ menuCardId: card.id })}>
              {card.name}
            </button>
          </li>
        ))}
      </ul>

      {gotchas.map((gotcha, index) => (
        <>{gotcha && <p key={index}>**{gotchas}</p>}</>
      ))}
    </div>
  )
}
